import { Badge, Button, Table, TableColumnType, TableProps } from 'antd';
import { officialTypeId, useStatusLabel, useTypeLabel } from '../hooks/useLabel';
import { useStoreNameConfig } from 'hooks/business/store/useStoreName';
import dayjs from 'lib/dayjs';
import { convertMessageToTextAndImage } from 'views/Conversation/Sider/ToDoBundle/helper';
import { filter } from 'lodash-es';
import {
  useLayoutEffect,
  useRef,
  useState,
  ReactNode,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { v4 as uuidV4 } from 'uuid';
import { createPortal } from 'react-dom';
import { useCreation } from 'ahooks';
import { getCurrencySymbol } from 'utils/getCurrencySymbol';
import { getTodoStillTime } from 'utils/renderStill';
import { useHisenseFeature } from 'hisense/featureFlag';

type RT = Todo.Record;

export interface TodoTableProps extends TableProps<RT> {
  onCheckDetail: (record: RT) => void;
  showSubType?: boolean;
  type?: Todo.Type;
  batchOpsNodeRender?: (records: RT[]) => ReactNode;
}

export interface TodoTableRef {
  clearSelectedRows: () => void;
}

const TodoTable = forwardRef<TodoTableRef, TodoTableProps>((props, ref) => {
  const { onCheckDetail, type, showSubType = false, batchOpsNodeRender, ...otherProps } = props;

  const { getTypeLabel, t } = useTypeLabel();

  const { statusLabel, getStatusLabel } = useStatusLabel();

  const { configMap: storeNameConfigMap } = useStoreNameConfig();

  const normalType = ![-998, -1, -999].includes(type);

  const isHisense = useHisenseFeature();

  const [selectedRows, setSelectedRows] = useState<RT[]>([]);

  const subTypeRender = (record: Todo.Record) => {
    const { subType = '', subType2 = '', subType3 = '' } = record;
    let str = subType;
    if (subType2) {
      str += ` / ${subType2}`;
      if (subType3) {
        str += ` / ${subType3}`;
      }
    }
    return str;
  };

  const hideDescriptionType = [7, 8, 9, 10, 11];
  const columns = filter<TableColumnType<RT>>(
    [
      !normalType
        ? {
            key: 'type',
            dataIndex: 'type',
            title: `${t('待办问题类型')}${showSubType ? ` / ${t('st_5')}` : ''}`,
            width: 200,
            render: (value, record) => {
              if (value === -999) {
                return `${t('marketing.已删除')}-${record.typeName}`;
              }
              if (value === -998) {
                return `${t('custom')}-${record.typeName}`;
              }
              return (
                <div>
                  <div>
                    {officialTypeId.includes(value) ? getTypeLabel(value) : record.typeName}
                  </div>
                  {showSubType && (
                    <div className="mt-[4px] text-[var(--fett-text-tertiary)]">
                      {subTypeRender(record)}
                    </div>
                  )}
                </div>
              );
            },
          }
        : undefined!,
      normalType
        ? {
            key: 'subType',
            dataIndex: 'subType',
            title: t('st_5'),
            width: 200,
            render: (_, record) => {
              return subTypeRender(record) || '-';
            },
          }
        : undefined!,
      hideDescriptionType.includes(type)
        ? undefined!
        : {
            key: 'description',
            dataIndex: 'description',
            title: t('备注'),
            width: 250,
            render: (description: RT['description']) => {
              const { text, images } = convertMessageToTextAndImage(description);

              return (
                <div className="max-w-[600px] whitespace-pre-wrap">
                  {`${text}${
                    images.length ? images.map(() => `[${t('phrase.图片')}]`).join('') : ''
                  }` || '-'}
                </div>
              );
            },
          },
      {
        key: 'relatedTradeId',
        dataIndex: 'relatedTradeId',
        title: t('关联订单号'),
        width: 250,
      },
      isHisense
        ? {
            key: 'relatedTraceNum',
            dataIndex: 'relatedTraceNum',
            title: t('TMS运单号'),
            width: 200,
            render: (value: string) => value || '—',
          }
        : undefined!,
      type === 7
        ? {
            key: 'compensationAmount',
            dataIndex: 'compensationAmount',
            width: 200,
            title: t('赔偿金额'),
            render: (_, record) => {
              const unit = getCurrencySymbol(record.currency);
              return (
                <div>
                  ${unit}&nbsp;{record.compensationAmount}
                </div>
              );
            },
          }
        : undefined!,
      [10, 11, 8].includes(type)
        ? {
            key: 'orderTime',
            dataIndex: 'relatedTraceNum',
            width: 250,
            title: t('物流单号'),
          }
        : undefined!,
      type === 8
        ? {
            key: 'freightAmount',
            dataIndex: 'freightAmount',
            width: 200,
            title: t('卖家支付运费金额'),
            render: (_, record) => {
              const unit = getCurrencySymbol(record.currency);
              return (
                <div>
                  {unit}&nbsp;{record.freightAmount}
                </div>
              );
            },
          }
        : undefined!,
      [9, 10].includes(type)
        ? {
            key: 'orderTime',
            dataIndex: 'orderTime',
            title: t('Entities.下单时间'),
            width: 200,
            render: (time) => {
              return <div>{dayjs(Number(time)).format('YYYY-MM-DD HH:mm')}</div>;
            },
          }
        : undefined!,
      type === 11
        ? {
            key: 'deliveryTime',
            dataIndex: 'deliveryTime',
            title: t('发货时间'),
            width: 200,
            render: (time) => {
              return <div>{dayjs(Number(time)).format('YYYY-MM-DD HH:mm')}</div>;
            },
          }
        : undefined!,
      type === 9
        ? {
            key: 'dengdaifahuoshichang',
            dataIndex: 'orderTime',
            title: t('等待发货时长'),
            width: 200,
            render: (_, record) => {
              const rt = getTodoStillTime(t, record);
              return rt ? <div className="text-[var(--fett-surface-error-text)]">{rt}</div> : '-';
            },
          }
        : undefined!,
      type === 10
        ? {
            key: 'dengdailanshoushichang',
            dataIndex: 'orderTime',
            title: t('等待揽收时长'),
            width: 200,
            render: (_, record) => {
              const rt = getTodoStillTime(t, record);
              return rt ? <div className="text-[var(--fett-surface-error-text)]">{rt}</div> : '-';
            },
          }
        : undefined!,
      type === 11
        ? {
            key: 'yunshushichang',
            dataIndex: 'deliveryTime',
            title: t('运输时长'),
            width: 200,
            render: (_, record) => {
              const rt = getTodoStillTime(t, record);
              return rt ? <div className="text-[var(--fett-surface-error-text)]">{rt}</div> : '-';
            },
          }
        : undefined!,
      {
        key: 'todoStatus',
        dataIndex: 'todoStatus',
        title: t('待办状态'),
        width: 100,
        render: (status: RT['todoStatus']) => {
          const label = statusLabel[status];

          if (!label) {
            return getStatusLabel(status);
          }

          return (
            <Badge
              status={
                (
                  {
                    1: 'error',
                    2: 'processing',
                    3: 'default',
                    4: 'warning',
                  } as const
                )[status]
              }
              text={label}
            />
          );
        },
      },
      {
        key: 'store',
        title: t('marketing.店铺'),
        width: 150,
        render: (_, { platform, region, storeName, storeAlias }) => {
          const isStoreAliasFirst = storeNameConfigMap[platform]?.[region];

          return isStoreAliasFirst ? storeAlias || storeName : storeName;
        },
      },
      {
        key: 'operatorName',
        dataIndex: 'operatorName',
        title: t('todo.受理客服'),
        width: 150,
      },
      {
        key: 'operation',
        title: t('操作'),
        width: 100,
        fixed: 'right',
        render: (_, record) => (
          <Button type="link" onClick={() => onCheckDetail(record)} style={{ paddingLeft: 0 }}>
            {t('feedback.详情')}
          </Button>
        ),
      },
    ],
    Boolean,
  );

  const id = useRef(uuidV4());
  const footerRef = useCreation(() => {
    const div = document.createElement('div');
    div.className = 'portal mr-auto ml-[-12px]';
    return div;
  }, []);
  useLayoutEffect(() => {
    const target = document.querySelector(`#h-${id.current} .ant-pagination`);
    if (target) {
      const portal = target.querySelector('.portal');
      if (!portal) {
        const first = target.firstElementChild;
        if (first) {
          target.insertBefore(footerRef, first);
        } else {
          target.appendChild(footerRef);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otherProps.dataSource]);

  // 暴露给父组件的方法
  useImperativeHandle(
    ref,
    () => ({
      clearSelectedRows: () => {
        setSelectedRows([]);
      },
    }),
    [],
  );

  return (
    <div id={`h-${id.current}`}>
      <Table<RT>
        rowKey="id"
        columns={columns}
        scroll={{ x: 'max-content' }}
        {...otherProps}
        rowSelection={
          normalType
            ? {
                fixed: 'left',
                columnWidth: 40,
                getCheckboxProps: (record) => ({
                  disabled: record.todoStatus === 3,
                }),
                selectedRowKeys: selectedRows.map((item) => item.id),
                onChange: (_, rows) => {
                  setSelectedRows(rows);
                },
              }
            : undefined
        }
      />
      {createPortal(
        selectedRows.length > 0 ? (
          <div className="flex items-center h-full">
            {t('aide.已选x项', { x: selectedRows.length })}
            {batchOpsNodeRender?.(selectedRows)}
          </div>
        ) : null,
        footerRef,
      )}
    </div>
  );
});

export default TodoTable;
